/*
 * @Description: 一般组件内部的部分，金额和门槛展示
 * @Author: AI Assistant
 * @Date: 2024-12-19
 */

import CountDown from '@/components/Countdown';
import Image from '@/components/Image';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { IS_DE } from '@/utils/constants';
import { env } from '@/utils/env';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { accSub } from '@/utils/tools';
import { cva } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import React, { useCallback, useMemo } from 'react';

const couponAmountVariants = cva('text-gray-800 font-be leading-[1]', {
  variants: {
    isWeb: {
      true: IS_DE ? 'text-[1.2rem]' : 'text-2xl',
      false: 'text-18',
    },
  },
  defaultVariants: {
    isWeb: true,
  },
});

const couponThresholdVariants = cva('text-gray-800', {
  variants: {
    isWeb: {
      true: 'text-xs mt-1.5',
      false: 'text-[10px] mt-[6px] leading-[1]',
    },
  },
  defaultVariants: {
    isWeb: true,
  },
});

const couponContainerVariants = cva('w-full', {
  variants: {
    isLoggedIn: {
      true: 'flex items-center',
      false: 'flex flex-col items-center justify-center bg-white',
    },
    isWeb: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      isWeb: true,
      isLoggedIn: true,
      className: 'h-[80px]',
    },
    {
      isWeb: true,
      isLoggedIn: false,
      className: 'h-[80px]',
    },
    {
      isWeb: false,
      isLoggedIn: true,
      className: 'h-[66px]',
    },
    {
      isWeb: false,
      isLoggedIn: false,
      className: 'h-[61px]',
    },
  ],
  defaultVariants: {
    isLoggedIn: false,
    isWeb: true,
  },
});

const couponDividerVariants = cva('', {
  variants: {
    isWeb: {
      true: '',
      false: '',
    },
    isLoggedIn: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      isWeb: true,
      isLoggedIn: true,
      className: 'h-[80px] w-20',
    },
    {
      isWeb: true,
      isLoggedIn: false,
      className: 'h-[80px] w-20',
    },
    {
      isWeb: false,
      isLoggedIn: true,
      className: 'h-[66px] w-[16.5px]',
    },
    {
      isWeb: false,
      isLoggedIn: false,
      className: 'h-[61px] w-[16.5px]',
    },
  ],
  defaultVariants: {
    isWeb: true,
    isLoggedIn: false,
  },
});

const couponDividerImageVariants = cva('', {
  variants: {
    isWeb: {
      true: '',
      false: '',
    },
    isLoggedIn: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      isWeb: true,
      isLoggedIn: true,
      className: 'h-[80px] w-20',
    },
    {
      isWeb: true,
      isLoggedIn: false,
      className: 'h-[80px] w-20',
    },
    {
      isWeb: false,
      isLoggedIn: true,
      className: 'h-[66px] w-[16.5px]',
    },
    {
      isWeb: false,
      isLoggedIn: false,
      className: 'h-[61px] w-[16.5px]',
    },
  ],
  defaultVariants: {
    isWeb: true,
    isLoggedIn: false,
  },
});

const couponDetailVariants = cva(
  'flex flex-col justify-center bg-white rounded-r-md pl-12 box-border',
  {
    variants: {
      isWeb: {
        true: '',
        false: '',
      },
      isLoggedIn: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        isWeb: true,
        isLoggedIn: true,
        className: 'h-[80px]',
      },
      {
        isWeb: true,
        isLoggedIn: false,
        className: 'h-[80px]',
      },
      {
        isWeb: false,
        isLoggedIn: true,
        className: 'h-[66px]',
      },
      {
        isWeb: false,
        isLoggedIn: false,
        className: 'h-[61px]',
      },
    ],
    defaultVariants: {
      isWeb: true,
      isLoggedIn: false,
    },
  },
);

const couponDetailTextVariants = cva('text-gray-800 font-bb', {
  variants: {
    isWeb: {
      true: 'text-base',
      false: 'text-14',
    },
    isDisabled: {
      true: 'text-gray-400',
      false: '',
    },
  },
  defaultVariants: {
    isWeb: true,
    isDisabled: false,
  },
});

const couponValidityVariants = cva('text-gray-500', {
  variants: {
    isWeb: {
      true: 'mt-8 text-xs',
      false: 'mt-6 text-[10px]',
    },
  },
  defaultVariants: {
    isWeb: true,
  },
});

const couponValueVariants = cva(
  'flex items-center justify-center bg-white rounded-l-md text-center box-border relative whitespace-nowrap font-be text-gray-800 leading-[1]',
  {
    variants: {
      isWeb: {
        true: '',
        false: '',
      },
      valueLength: {
        true: '',
        false: '',
      },
      isDisabled: {
        true: '!text-gray-400',
        false: '!text-primary-purple',
      },
      isLoggedIn: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        isWeb: true,
        isLoggedIn: true,
        valueLength: true,
        className: 'h-[80px] w-[22.3%] text-xl',
      },
      {
        isWeb: true,
        isLoggedIn: true,
        valueLength: false,
        className: `h-[80px] w-[22.3%] ${['ca', 'de'].includes(env.NEXT_PUBLIC_SITE_NAME) ? 'text-[1.2rem]' : 'text-2xl'}`,
      },
      {
        isWeb: true,
        isLoggedIn: false,
        valueLength: true,
        className: 'h-[80px] w-[22.3%] text-xl',
      },
      {
        isWeb: true,
        isLoggedIn: false,
        valueLength: false,
        className: `h-[80px] w-[22.3%] ${['ca', 'de'].includes(env.NEXT_PUBLIC_SITE_NAME) ? 'text-[1.2rem]' : 'text-2xl'}`,
      },
      {
        isWeb: false,
        isLoggedIn: true,
        valueLength: true,
        className: `h-[66px] w-[25%] ${IS_DE ? 'text-18' : 'text-14'}`,
      },
      {
        isWeb: false,
        isLoggedIn: true,
        valueLength: false,
        className: 'h-[66px] w-[25%] text-18',
      },
      {
        isWeb: false,
        isLoggedIn: false,
        valueLength: true,
        className: `h-[61px] w-[25%] ${IS_DE ? 'text-18' : 'text-14'}`,
      },
      {
        isWeb: false,
        isLoggedIn: false,
        valueLength: false,
        className: 'h-[61px] w-[25%] text-18',
      },
    ],
    defaultVariants: {
      isWeb: true,
      valueLength: false,
      isDisabled: false,
      isLoggedIn: false,
    },
  },
);

// 优惠券标签样式接口
export interface CouponLabel {
  coupon_tag?: number; // 优惠券标签 1:无 2:新客首单 3:S级活动
  tag_text?: string; // 标签文案
  text_color?: string; // 文案颜色
  bg_color_type?: number; // 背景颜色类型 1:纯色 2:渐变
  bg_color?: string; // 背景颜色（纯色）
  bg_start_color?: string; // 起点颜色（渐变）
  bg_end_color?: string; // 终点颜色（渐变）
}

// 优惠券项目接口
export interface CouponItem {
  way: number;
  is_get: number;
  code_status?: number;
  is_first_order?: number;
  count_down?: number;
  twenty_four_count_time?: number;
  time_interval?: string[];
  discounts_details?: any;
  full_discounts_details?: any;
  appoint_discounts_details?: any;
  free_discounts_details?: any;
  id: number;
  extension_field?: {
    coupon_label?: CouponLabel;
  };
}

// 优惠券详情接口
export interface CouponDetails {
  discounts_value: number | string;
  discounts_threshold_type?: number;
  discounts_threshold?: number | string;
  discounts_type?: number;
  number?: number;
}

// 普通优惠券子组件
export interface NormalCouponProps {
  item: CouponItem;
  details: CouponDetails;
  status: number;
  couponType: number;
  isShowCountDown: boolean;
  isShowValidDate: boolean;
  validText: string;
  onCountDownFinish?: () => void;
  dividerBgColor?: string; // 分隔线背景色，用于VIP优惠券等特殊场景
  isWeb: boolean;
}

const NormalCoupon: React.FC<NormalCouponProps> = ({
  item,
  details,
  status,
  couponType,
  isShowCountDown,
  isShowValidDate,
  validText,
  onCountDownFinish,
  dividerBgColor,
  isWeb,
}) => {
  const couponsT = useTranslations('coupons');
  const t = useTranslations('components.coupon');
  const token = useStore((state) => state.token);
  const currency = useStore((state) => state.currency);
  logger.log(isShowValidDate, 'isShowValidDate');

  // 优惠券$7off 15%off的文案
  const offText = useMemo(() => {
    const { discounts_value: value } = details;

    const formatMap = {
      1: {
        // 折扣
        withToken: `${value}%<br/>${couponsT('off')}`,
        withoutToken: `${value}% ${couponsT('off')}`,
      },
      2: {
        // 满减
        withToken: `${formatPriceNumberWithSymbol(`${value}`, currency, false, 2, false)}<br/>${couponsT('off')}`,
        withoutToken: `${formatPriceNumberWithSymbol(`${value}`, currency, false, 2, false)} ${couponsT('off')}`,
      },
      3: {
        // 指定价
        withToken: `<span style="font-size: 12px;">SALE PRICE</span><br/>${formatPriceNumberWithSymbol(`${value}`, currency, false, 2, false)}`,
        withoutToken: `SALE PRICE ${formatPriceNumberWithSymbol(`${value}`, currency, false, 2, false)}`,
      },
      4: {
        // 免费
        withToken: 'FREE',
        withoutToken: 'FREE',
      },
    };

    const format = formatMap[couponType as keyof typeof formatMap] || formatMap[2]; // 默认使用满减格式
    return token ? format.withToken : format.withoutToken;
  }, [details, couponType, token]);

  // 优惠文字的长度
  const valueLength = useMemo(() => {
    const text = details.discounts_value;
    return `${text}`.length;
  }, [details.discounts_value]);

  // 门槛文案
  const holdText = useMemo(() => {
    const siteWide = t('sitewideSale');
    const { discounts_threshold_type, discounts_threshold, discounts_value } = details;
    let discountThresholdText;

    if (couponType === 1) {
      // 折扣
      // discounts_threshold_type 一次性折扣使用门槛 类型 1满金额减 2满件数减
      const thresholdType = Number(discounts_threshold_type);
      const thresholdValue = Number(discounts_threshold);
      if (thresholdType === 1) {
        discountThresholdText =
          thresholdValue === 0.01
            ? siteWide
            : `${t('ordersOver')} ${formatPriceNumberWithSymbol(`${thresholdValue}`, currency, false, 2, false)}`;
      } else {
        discountThresholdText =
          thresholdValue === 1 ? siteWide : `${thresholdValue}+ ${t('items')}`;
      }
    } else if (
      Number(accSub(Number(discounts_threshold || 0), Number(discounts_value || 0))) === 0.01
    ) {
      // 满减，门槛为1元时
      discountThresholdText = siteWide;
    } else {
      discountThresholdText = `${t('ordersOver')}  ${formatPriceNumberWithSymbol(`${discounts_threshold}`, currency, false, 2, false)}`;
    }

    // 登录以后的文案，需要在前面加上For Orders(件数优惠折扣除外)
    if (token && discountThresholdText !== siteWide && !discountThresholdText.includes('Above')) {
      discountThresholdText = `${discountThresholdText}`;
    }

    if (couponType === 3) {
      discountThresholdText = `${t('onSelectedItems')}`;
    }

    if (couponType === 4) {
      discountThresholdText = `${details.discounts_type === 1 ? t('highestPriced') : t('lowestPriced')} ${details.number} ${details.number === 1 ? t('item') : t('items')}`;
    }

    return discountThresholdText;
  }, [details, couponType, token]);

  const handleCountDownFinish = useCallback(() => {
    onCountDownFinish?.();
  }, [onCountDownFinish]);

  return (
    <div className={couponContainerVariants({ isLoggedIn: !!token, isWeb })}>
      {/* 登录状态下的优惠券展示为长条形状，中间是异形虚线 */}
      {token ? (
        <>
          {/* 优惠金额部分 */}
          <div
            className={couponValueVariants({
              isWeb,
              valueLength: valueLength >= 5,
              isDisabled: status === 3,
              isLoggedIn: !!token,
            })}
          >
            <span
              className={`${valueLength >= 5 ? 'transform translate-x-1 scale-80' : 'transform translate-x-1'}`}
              dangerouslySetInnerHTML={{ __html: offText }}
              style={{ letterSpacing: -1 }}
            />
          </div>

          {/* 中间分隔线 */}
          <div
            className={couponDividerVariants({ isWeb, isLoggedIn: !!token })}
            style={{
              backgroundColor: dividerBgColor || '#E4E8FA',
            }}
          >
            {/* 从之前的同一个Image里src区分的写法改成这种，看着是能够避免加载延迟的问题，不至于看起来中间断了的效果 */}
            {status === 3 ? (
              <Image
                className={couponDividerImageVariants({ isWeb, isLoggedIn: !!token })}
                src={'/coupon/gray_middle.png'}
                width={isWeb ? 20 : 16.5}
                height={isWeb ? 80 : 61}
                alt="coupon divider"
                priority
              />
            ) : (
              <Image
                className={couponDividerImageVariants({ isWeb, isLoggedIn: !!token })}
                src={'/coupon/blue_middle.png'}
                width={isWeb ? 20 : 16.5}
                height={isWeb ? 80 : 61}
                alt="coupon divider"
                priority
              />
            )}
          </div>

          {/* 门槛和有效期部分 */}
          <div
            className={couponDetailVariants({ isWeb, isLoggedIn: !!token })}
            style={{ width: 'calc(100% - 22.3% - 20px)' }}
          >
            <span
              className={`${couponDetailTextVariants({ isWeb, isDisabled: status === 3 })} leading-[1em]`}
            >
              {holdText}
            </span>

            {isShowCountDown && (
              <div className={couponValidityVariants({ isWeb })}>
                <CountDown
                  time={item.twenty_four_count_time || 0}
                  onCountDownFinish={handleCountDownFinish}
                  className="text-primary-purple text-xs font-br w-[100px]"
                  showTimeUnit
                />
              </div>
            )}

            {!isShowCountDown && validText && (
              <div className={`${couponValidityVariants({ isWeb })} leading-[1em]`}>
                {validText}
              </div>
            )}
          </div>
        </>
      ) : (
        /* 未登录状态的垂直布局 */
        <>
          {/* 优惠金额 */}
          <div className={couponAmountVariants({ isWeb })}>
            <span dangerouslySetInnerHTML={{ __html: offText }} style={{ letterSpacing: -1 }} />
          </div>

          {/* 门槛文案 */}
          <div className={couponThresholdVariants({ isWeb })}>{holdText}</div>
        </>
      )}
    </div>
  );
};

export default NormalCoupon;
